import i18n from '@/utils/i18n';
import { AccountSelector } from '@/experiences/sales-portal/components/account-selector';
import { AppPageLayout } from '@experiences/templates/app-page-layout';
import { CreateOrderGuideDialogContainer } from '@/experiences/sales-portal/domain/order-guides/screens/order-guides-list/containers/create-order-guide-dialog';
import { createStyles } from '@/contexts/theme-context';
import { EmptyState } from '@experiences/components/empty-state';
import { ErrorState } from '@/experiences/sales-portal/components/error-state';
import { Icons } from '@usepepper/sprout-kit-ui';
import { navigateToSalesPortalRoute } from '@/experiences/sales-portal/sales-portal-route-config';
import {
  ORDER_GUIDE_HEADER_ACTIONS_PORTAL_HOST,
  ORDER_GUIDE_PANEL_PORTAL_HOST,
} from '@/experiences/sales-portal/domain/order-guides/screens/order-guides-list/order-guides-list-constants';
import { OrderGuide } from '@/experiences/sales-portal/domain/order-guides/blocs/types';
import { OrderGuideCollection } from '@/experiences/sales-portal/domain/order-guides/screens/order-guides-list/types';
import { OrderGuideItemsSkeleton } from '@/experiences/sales-portal/domain/order-guides/screens/order-guides-list/components/order-guide-items-skeleton';
import { OrderGuideSelectorDropdown } from '@/experiences/sales-portal/domain/order-guides/screens/order-guides-list/components/order-guide-selector-dropdown';
import { OrderGuideSelectorsSkeleton } from '@/experiences/sales-portal/domain/order-guides/screens/order-guides-list/components/order-guide-selectors-skeleton';
import { OrderGuidesListContent } from '@/experiences/sales-portal/domain/order-guides/screens/order-guides-list/containers/order-guides-list-content';
import { partnerSupplier } from '@/utils/environment';
import { PortalHost } from '@gorhom/portal';
import { SALES_PORTAL_ROUTES } from '@/navigators/routes';
import {
  SalesHubAccountOption,
  useAccountListLoader,
} from '@/experiences/sales-portal/blocs/loaders/use-account-list-loader';
import { SalesHubShellTemplate } from '@/experiences/sales-portal/templates/sales-hub-shell-template';
import { SalesPortalOrderGuideStackParams } from '@/navigators/types';
import { StackScreenProps } from '@react-navigation/stack';
import { useOrderGuideCollectionsLoader } from '@/experiences/sales-portal/domain/order-guides/screens/order-guides-list/blocs/loaders/use-order-guide-collections-loader';
import { useOrderGuideEditability } from '@/experiences/sales-portal/domain/order-guides/blocs/hooks/use-order-guide-editability';
import { useState } from 'react';
import { View } from 'react-native';

const HEADER_HEIGHT = 48;
const EMPTY_STATE_ICON_SIZE = 32;

type Props = StackScreenProps<
  SalesPortalOrderGuideStackParams,
  typeof SALES_PORTAL_ROUTES.OrderGuides
>;

/**
 * Order Guide management screen. Reads the selected account + active order
 * guide from the route, loads the distributor's accounts and (once an account
 * is selected) that customer's order guides, and writes selections back to the
 * route.
 */
export const OrderGuidesListScreen: React.FC<Props> = ({
  navigation,
  route,
}) => {
  const { orderGuideUUID, partitionUUID, restaurantUUID, supplierUUID } =
    route.params;
  const [styles, theme] = useStyles();
  const [isAccountSelectorOpen, setIsAccountSelectorOpen] = useState(false);
  const [isAddItemsOpen, setIsAddItemsOpen] = useState(false);
  const [isConvertOpen, setIsConvertOpen] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  // The create mutation returns the new (empty) guide; we hold it here to skip
  // loading its items on the first navigation to it.
  const [createdOrderGuide, setCreatedOrderGuide] = useState<OrderGuide | null>(
    null,
  );
  const {
    accounts,
    error: accountsError,
    loading: accountsLoading,
    refetch: refetchAccounts,
  } = useAccountListLoader(partnerSupplier.businessOrganizationUUID);
  const {
    editabilityConfig,
    orderGuides,
    error: orderGuidesError,
    loading: orderGuidesLoading,
    refetch: refetchOrderGuides,
  } = useOrderGuideCollectionsLoader({
    restaurantUUID,
    supplierUUID,
  });

  const selectedAccount =
    accounts.find(
      (account) =>
        account.restaurantUUID === restaurantUUID &&
        account.supplierUUID === supplierUUID,
    ) ?? null;

  const selectedGuideFromList =
    orderGuides.find((guide) => guide.uuid === orderGuideUUID) ?? null;

  const defaultGuide =
    orderGuides.find(
      (guide) => guide.isDefault && guide.kind === 'restaurant',
    ) ?? null;

  // A newly created guide isn't in the OG list until the refetch returns, so
  // build its collection entry from the create response to keep it selected.
  const createdGuideCollection: OrderGuideCollection | null = createdOrderGuide
    ? {
        displayName: createdOrderGuide.name ?? i18n.t('order_guide'),
        groupCount: createdOrderGuide.partitions.length,
        isDefault: createdOrderGuide.isDefault,
        kind: 'restaurant',
        linkedSupplierCollectionUUID: null,
        sortOrder: null,
        uuid: createdOrderGuide.collectionUUID,
      }
    : null;

  const createdGuide =
    createdGuideCollection?.uuid === orderGuideUUID
      ? createdGuideCollection
      : null;

  const selectedOrderGuide =
    selectedGuideFromList ?? createdGuide ?? defaultGuide;

  // Feed the created guide's contents only to the guide it was created for
  const createdGuideForSelection =
    createdOrderGuide &&
    createdOrderGuide.collectionUUID === selectedOrderGuide?.uuid
      ? createdOrderGuide
      : undefined;

  const loading =
    (accountsLoading && accounts.length === 0) ||
    (!!selectedAccount && orderGuidesLoading);

  const handleSelectAccount = (account: SalesHubAccountOption) => {
    setCreatedOrderGuide(null);
    navigation.setParams({
      orderGuideUUID: '',
      restaurantUUID: account.restaurantUUID,
      supplierUUID: account.supplierUUID,
    });
  };

  const handleSelectOrderGuide = (orderGuide: OrderGuideCollection) => {
    if (orderGuide.uuid === selectedOrderGuide?.uuid) {
      return;
    }
    setCreatedOrderGuide(null);
    navigation.setParams({ orderGuideUUID: orderGuide.uuid });
  };

  const handleOrderGuideCreated = async (newOrderGuide: OrderGuide) => {
    // Select the new guide before refetching so it shows immediately without
    // an items fetch.
    setCreatedOrderGuide(newOrderGuide);
    navigation.setParams({ orderGuideUUID: newOrderGuide.collectionUUID });
    await refetchOrderGuides();
  };

  const handleConvertedToQuote = (priceQuoteId: string) => {
    setIsConvertOpen(false);
    navigateToSalesPortalRoute(
      navigation,
      SALES_PORTAL_ROUTES.PriceQuotesBuilder,
      { priceQuoteUUID: priceQuoteId },
    );
  };

  const hasNoAccounts = !accountsLoading && accounts.length === 0;
  const hasError =
    !!accountsError ||
    hasNoAccounts ||
    (!!selectedAccount && !!orderGuidesError);
  const handleRetry = () => {
    if (accountsError || hasNoAccounts) {
      void refetchAccounts();
      return;
    }
    if (orderGuidesError) {
      void refetchOrderGuides();
    }
  };

  const {
    editability,
    isOrderGuideDistributorLocked,
    isOrderGuideViewOnly,
    loading: editabilityLoading,
  } = useOrderGuideEditability({
    businessOrganizationUUID: partnerSupplier.businessOrganizationUUID,
    config: editabilityConfig,
    orderGuide: selectedOrderGuide,
    supplierUUID,
  });

  const orderGuideOptions = orderGuides.map((guide) => ({
    ...guide,
    isViewOnly: isOrderGuideViewOnly(guide),
  }));

  const isSelectionLoading =
    loading || (!!selectedOrderGuide && editabilityLoading);

  const canShowHeaderActions =
    !hasError &&
    !!selectedAccount &&
    !!selectedOrderGuide &&
    !isSelectionLoading;

  return (
    <SalesHubShellTemplate>
      <AppPageLayout
        actions={
          canShowHeaderActions ? (
            <PortalHost name={ORDER_GUIDE_HEADER_ACTIONS_PORTAL_HOST} />
          ) : undefined
        }
        mode="focused"
        scroll={false}
        subBar={
          hasError ? undefined : (
            <View style={styles.header}>
              {loading ? (
                <OrderGuideSelectorsSkeleton />
              ) : (
                <>
                  <AccountSelector
                    account={selectedAccount}
                    accounts={accounts}
                    isOpen={isAccountSelectorOpen}
                    onOpenChange={setIsAccountSelectorOpen}
                    onSelectAccount={handleSelectAccount}
                  />
                  {selectedAccount ? (
                    <OrderGuideSelectorDropdown
                      // a customer can't edit order guides (because of a chat level lock, merge strategy or otherwise)
                      // they can't create either
                      canCreateGuides={editability !== 'allGuidesLocked'}
                      onCreateOrderGuide={() => setIsCreateDialogOpen(true)}
                      onSelectOrderGuide={handleSelectOrderGuide}
                      orderGuides={orderGuideOptions}
                      selectedOrderGuide={selectedOrderGuide}
                    />
                  ) : null}
                </>
              )}
            </View>
          )
        }
        title={i18n.t('sales_portal.order_guides_list.title')}
      >
        <View style={styles.panel}>
          {hasError ? (
            <ErrorState
              ctaProps={{ disabled: accountsLoading || orderGuidesLoading }}
              description={i18n.t('sales_portal.error.description')}
              onPress={handleRetry}
              title={i18n.t('sales_portal.error.title')}
            />
          ) : isSelectionLoading ? (
            <OrderGuideItemsSkeleton />
          ) : selectedAccount && selectedOrderGuide ? (
            <OrderGuidesListContent
              accountName={selectedAccount.name}
              chatUUID={selectedAccount.chatId}
              collectionUUID={selectedOrderGuide.uuid}
              createdOrderGuide={createdGuideForSelection}
              editability={editability}
              initialPartitionUUID={partitionUUID}
              isAddItemsOpen={isAddItemsOpen}
              isConvertToQuoteOpen={isConvertOpen}
              isDistributorLocked={isOrderGuideDistributorLocked(
                selectedOrderGuide,
              )}
              onCloseAddItems={() => setIsAddItemsOpen(false)}
              onCloseConvertToQuote={() => setIsConvertOpen(false)}
              onConvertedToQuote={handleConvertedToQuote}
              onOpenAddItems={() => setIsAddItemsOpen(true)}
              onOpenConvertToQuote={() => setIsConvertOpen(true)}
              onRefreshGuides={() => void refetchOrderGuides()}
              restaurantUUID={selectedAccount.restaurantUUID}
              supplierUUID={selectedAccount.supplierUUID}
            />
          ) : (
            <EmptyState
              description={i18n.t(
                'sales_portal.order_guides_list.items.no_selection_description',
              )}
              iconElement={
                <Icons.Book
                  color={theme.sk.color.icon.tertiary}
                  size={EMPTY_STATE_ICON_SIZE}
                />
              }
              title={i18n.t(
                'sales_portal.order_guides_list.items.no_selection_title',
              )}
            />
          )}
          <PortalHost name={ORDER_GUIDE_PANEL_PORTAL_HOST} />
          <CreateOrderGuideDialogContainer
            existingNames={orderGuides.map((guide) => guide.displayName)}
            isOpen={isCreateDialogOpen}
            onCreated={handleOrderGuideCreated}
            onOpenChange={setIsCreateDialogOpen}
            restaurantUUID={restaurantUUID}
            supplierUUID={supplierUUID}
          />
        </View>
      </AppPageLayout>
    </SalesHubShellTemplate>
  );
};

const useStyles = createStyles((theme) => ({
  header: {
    alignItems: 'center',
    borderBottomWidth: 1,
    borderColor: theme.sk.color.divider.default,
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSmall,
    height: HEADER_HEIGHT,
  },
  panel: {
    flex: 1,
  },
}));
